import { useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router';
import ReactHTMLParser from 'react-html-parser';

import { AccountNav } from '../Account/AccountNav';
import { Button, Card, Icon, Loading } from '../../components/UI';
import { DropdownMenu } from '../../components/form';

import {
  LIST_KNOWLEDGE_CATEGORIES,
  SEARCH_KNOWLEDGE_POSTS,
} from '../../http/apollo-queries';

import { ListKnowledgeCategories, Post } from './types';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../helpers/analytics';
import { truncateString } from '../../helpers/functions';

const Support = () => {
  const navigate = useNavigate();
  const [searchWord, setSearchWord] = useState('');
  const [index, setIndex] = useState(0);
  const { i18n } = useTranslation();

  const selectedPost = useRef<HTMLDivElement | null>(null);
  const scrollTo = () => {
    if (selectedPost?.current) {
      selectedPost.current.scrollIntoView();
    }
  };

  const [
    search,
    {
      data: { listKnowledgePosts: { items: searchItems = [] } = {} } = {},
      loading: searchLoading,
    },
  ] = useLazyQuery(SEARCH_KNOWLEDGE_POSTS, { fetchPolicy: 'network-only' });

  const dropDownItems = searchItems.length
    ? searchItems.map(({ title }) => title)
    : ['No results'];

  const {
    loading,
    data: { listKnowledgeCategories: { items = [] } = {} } = {},
  } = useQuery<ListKnowledgeCategories>(LIST_KNOWLEDGE_CATEGORIES);

  const posts = items.reduce(
    (posts, { name, knowledgePosts: { items } }) =>
      posts.concat(items.map((item) => ({ name, ...item }))),
    [] as Post[]
  );

  const isDisabledPrevious = index === 0;
  const isDisabledNext = index === posts.length - 1;

  const handleOnChange = ({ target: { value } }) => {
    setSearchWord(value);

    value.length > 2 &&
      search({
        variables: { term: value, language: i18n.language.toUpperCase() },
      });
  };

  const handlePosition = (idx: number) => {
    scrollTo();
    idx >= 0 && idx < posts.length && setIndex(idx);
  };

  const postsList = posts.map(({ name, id, title, position }, idx) => {
    const elements = [] as JSX.Element[];

    position === 1 &&
      elements.push(
        <h3 key={name} className="text-grey-50 text-xs pl-5 py-5 uppercase">
          {name}
        </h3>
      );

    elements.push(
      <p
        className={`
          text-sm
          mb-1
          px-5
          py-1
          cursor-pointer
          ${idx === index && 'font-bold'}
        `}
        key={id}
        onClick={() => handlePosition(idx)}
      >
        {title}
      </p>
    );

    return elements;
  });

  const handleClickOnDropDown = (title: string) => {
    const idx = posts.findIndex((post) => post.title === title);

    idx > -1 && handlePosition(idx);
    setSearchWord('');
  };

  return (
    <div
      className={`flex flex-col w-full bg-white text-black items-center dark:bg-black-real dark:text-white`}
    >
      <div className="w-full lg:w-8/12X">
        <AccountNav />
        <Loading className={'mt-4 mx-auto'} status={loading} />
        <Card title="Guide" className={`my-8`} padding={false}>
          <div className={'flex flex-col md:flex-row'}>
            <div
              className={`flex flex-col w-full md:w-1/4 justify-between border-r border-opacity-50 border-grey-30`}
            >
              <div>
                {/* search */}
                <div
                  className={`flex items-center pl-5 border-b border-grey-30 h-16 dark:border-grey-80`}
                >
                  <Icon svgName={'search'} />
                  <input
                    placeholder="Search"
                    className="w-40 ml-2 dark:bg-grey-90"
                    onChange={handleOnChange}
                    value={searchWord}
                  />
                  {searchLoading ? (
                    <div className="flex items-center">
                      <Loading
                        status={searchLoading}
                        className="w-0 items-center -ml-32 mt-10"
                      />
                    </div>
                  ) : (
                    <DropdownMenu
                      dropdownIconClassName="w-0 items-center -ml-28 mt-8"
                      dropdownItems={dropDownItems}
                      handleClick={handleClickOnDropDown}
                      isDropdownVisible={searchWord.length > 2}
                      hideOnItemClick
                      dropdownTooltipClassName={`bg-white border-solid border-grey-10 border-2 shadow-md `}
                    />
                  )}
                </div>
                <div className="flex flex-col h-auto md:h-150 overflow-y-scroll ">
                  {postsList}
                </div>
              </div>
            </div>

            {/* selected post */}
            <div className="flex flex-col w-full md:w-3/4" ref={selectedPost}>
              <div className={'px-5 pt-5 flex-grow-1'}>
                <p
                  className={`uppercase font-normal text-grey-50 text-xs mb-1`}
                >
                  {posts[index]?.name}
                </p>
                <p className="font-medium text-lg my-1">
                  {posts[index]?.title}
                </p>
                <div className="mb-4 overflow-y-auto h-80">
                  {ReactHTMLParser(posts[index]?.content)}
                </div>

                {/* ask for help */}
                <p className="text-sm mb-3 mt-6 font-semibold">
                  Can't find the answer you are looking for?
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  buttonClass="w-32"
                  onClick={() => {
                    trackEvent('support_contact_us');
                    navigate('/contacts');
                  }}
                >
                  Contact us
                </Button>
              </div>

              {/* navigation */}
              <div
                className={`flex h-24 mt-6 border-t border-grey-30 items-center dark:border-grey-80 `}
              >
                {/* back button */}
                <button
                  className={`
                    flex
                    w-full
                    h-full
                    pl-5
                    pr-4 md:pr-10
                    ${
                      isDisabledPrevious &&
                      `
                        bg-grey-10
                        text-grey-30
                        h-full
                        font-bold
                        cursor-not-allowed
                        dark:bg-grey-80
                      `
                    }
                  `}
                  onClick={() => handlePosition(index - 1)}
                >
                  <div className="flex items-center h-full pt-2 w-min">
                    <Icon svgName={'arrowBack'} />
                  </div>
                  <div className="flex flex-col w-full h-full items-center">
                    <p className="w-full text-right text-grey-50 text-xs mt-2">
                      PREVIOUS
                    </p>
                    <div className="flex w-full h-full text-right">
                      <h4 className="my-auto px-2 text-right w-full text-xs md:text-base">
                        {isDisabledPrevious
                          ? ''
                          : truncateString(posts[index - 1]?.title || '', 30)}
                      </h4>
                    </div>
                  </div>
                </button>

                <div className="h-full border-r border-grey-30 dark:border-grey-80" />

                {/* forward button */}
                <button
                  className={`
                    flex
                    h-full
                    w-full
                    pr-5
                    pl-4 md:pl-10
                    
                    ${
                      isDisabledNext &&
                      `
                        bg-grey-10
                        text-grey-30
                        h-full
                        font-bold
                        cursor-not-allowed
                        dark:bg-grey-80
                      `
                    }
                  `}
                  onClick={() => handlePosition(index + 1)}
                >
                  <div className="flex flex-col w-full h-full items-center">
                    <p className="w-full text-left text-grey-50 text-xs mt-2">
                      NEXT
                    </p>
                    <div className="flex w-full h-full text-left">
                      <h4 className="my-auto px-2 text-left w-full text-xs md:text-base">
                        {isDisabledNext
                          ? ''
                          : truncateString(posts[index + 1]?.title || '', 30)}
                      </h4>
                    </div>
                  </div>
                  <div className="flex items-center h-full pt-2 w-min">
                    <Icon svgName={'arrowNext'} />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Support;
